import type { CdkDragDrop } from '@angular/cdk/drag-drop';
import { CdkDrag, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { AsyncPipe, NgTemplateOutlet, SlicePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  signal,
  viewChildren,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import type { Observable } from 'rxjs';
import { combineLatest, filter, map, merge, tap } from 'rxjs';

import {
  debounceAsap,
  ElementInjectorDirective,
  filterBoolean,
  NgTemplateAssertType,
} from '@core/common-legacy';
import {
  SidebarComponent,
  SidebarHeaderComponent,
  SidebarItemComponent,
} from '@core/common-smart-ui';
import { TranslatePipe } from '@core/i18n-legacy';
import type { IconName } from '@core/user-interface-legacy';
import {
  BadgeComponent,
  ButtonComponent,
  DividerComponent,
  IconColorDirective,
  IconComponent,
  AddNewButtonComponent,
} from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';
import type {
  BaseView,
  DataSource,
  DataSourceService,
  View,
  ViewGroup,
  ViewListItem,
  ViewPosition,
} from '@core/views-types';
import { ViewSymbolComponent } from '@core/views-ui';
import { ViewBaseRoute, dataSource$, dataSourceService$ } from '@core/views-util';

import { ViewsAnalytics } from '../../../analytics/views-analytics';
import { ViewDataService } from '../../view-data.service';
import { ViewsLocalStorageService } from '../local-storage.service';
import { ViewPageService } from '../view-page.service';

@Component({
  selector: 'rt-views-view-page-sidebar',
  imports: [
    SidebarComponent,
    SidebarItemComponent,
    DividerComponent,
    BadgeComponent,
    TranslatePipe,
    AsyncPipe,
    CdkDropList,
    CdkDrag,
    ViewSymbolComponent,
    NgTemplateAssertType,
    ButtonComponent,
    IconComponent,
    IconColorDirective,
    AddNewButtonComponent,
    SlicePipe,
    NgTemplateOutlet,
    ElementInjectorDirective,
    SidebarHeaderComponent,
  ],
  templateUrl: './view-page-sidebar.component.html',
  styleUrls: ['./view-page-sidebar.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewPageSidebarComponent {
  private readonly viewPageService = inject(ViewPageService);

  private readonly DEFAULT_VISIBLE_ITEMS_THRESHOLD = 10;

  public readonly MAX_INT: number = Number.MAX_SAFE_INTEGER;
  public readonly TItemLeftSlot: { view: BaseView; active: boolean; iconName?: IconName };
  public readonly TSidebarItem: {
    first: boolean;
    view: BaseView;
    draggable: boolean;
    isInGroup: boolean;
    iconName?: IconName;
  };
  public readonly TSidebarGroup: { first: boolean; view: ViewGroup };

  public views$: Observable<ViewListItem[]> = combineLatest([
    this.viewPageService.viewList$,
    this.viewPageService.currentView$,
  ]).pipe(
    debounceAsap(),
    tap(([views, currentView]) => {
      if (!currentView) return;

      const activeGroup = views
        .filter(view => view.type === 'view_group')
        .find((group: ViewGroup) => group.views.some(view => view.id === currentView.id));
      this.activeGroupViewId = activeGroup?.id ?? null;
    }),
    map(([views]) => views),
  );
  public dataSource: DataSource;
  public currentView = signal<View | null>(null);
  public showAll = false;
  public isSidebarOpen: boolean;
  public activeViewRunAnimation = false;
  public dataSourceService: DataSourceService;
  public viewListCountersMap: Record<View['id'], number> = {};
  public expandedGroups: Record<ViewGroup['id'], boolean> = {};
  public hoverItemId: View['id'] | null = null;
  public activeGroupViewId: View['id'] | null = null;
  public showLimit: number;

  private router = inject(Router);
  private baseRoute = inject(ViewBaseRoute);
  private cdRef = inject(ChangeDetectorRef);
  private dataSource$ = inject(dataSource$);
  private dataSourceService$ = inject(dataSourceService$);
  private viewDataService = inject(ViewDataService);
  private localStorageService = inject(ViewsLocalStorageService);
  private viewsAnalytics = inject(ViewsAnalytics);
  private untilDestroyed = injectUntilDestroyed();

  private sidebarItemComponents = viewChildren<SidebarItemComponent>('sidebarItemCmp');

  constructor() {
    this.scrollIntoActiveView();
  }

  public ngOnInit() {
    this.isSidebarOpen = this.localStorageService.viewPageSidebar.get()?.isOpen ?? true;

    this.watchViewData();
    this.watchNavigationEnd();
    this.watchCounters();
    this.watchExpandedGroups();
  }

  public onCreateView() {
    this.viewsAnalytics.newViewClick({ dataSourceId: this.dataSource.id });
    this.router.navigate([this.baseRoute, 'create']);
  }

  public onViewSelect(view: ViewListItem) {
    if (view.type === 'view') {
      this.navigateToView(view.id);
    } else {
      const { value } = this.viewPageService.expandedSidebarGroups$;
      this.viewPageService.expandedSidebarGroups$.next({
        ...value,
        [view.id]: !value[view.id],
      });
    }
  }

  public toggleShowAll() {
    this.showAll = !this.showAll;
    this.cdRef.detectChanges();
  }

  public onSidebarExpand(isOpen: boolean): void {
    this.localStorageService.viewPageSidebar.set({ isOpen });
    this.isSidebarOpen = isOpen;
    this.cdRef.detectChanges();
  }

  public onReorder(event: CdkDragDrop<ViewListItem>, views: ViewListItem[] | null) {
    if (views === null) {
      return;
    }

    const temp = [...views];
    moveItemInArray(temp, event.previousIndex, event.currentIndex);
    const positions = temp.map(
      ({ id, codename }, index): ViewPosition => ({ viewId: id, codename, position: index }),
    );
    this.viewPageService.changePositions(this.dataSource.id, positions);
  }

  public onItemMouseEnter(view: ViewListItem) {
    this.hoverItemId = view.id;
    this.cdRef.detectChanges();
  }

  public onItemMouseLeave(view: ViewListItem) {
    this.hoverItemId = null;
    this.cdRef.detectChanges();
  }

  private watchNavigationEnd() {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        this.untilDestroyed,
      )
      .subscribe(() => {
        this.activeViewRunAnimation = !!history.state.activeViewRunAnimation;
        if (this.activeViewRunAnimation) {
          history.replaceState({ ...history.state, activeViewRunAnimation: false }, '');
        }
      });
  }

  private watchCounters() {
    combineLatest([
      this.viewPageService.viewListCountersMap$,
      this.viewDataService.currentFiltersRecordsCount$,
    ])
      .pipe(this.untilDestroyed)
      .subscribe(([countersMap]) => {
        this.viewListCountersMap = countersMap;
        this.cdRef.detectChanges();
      });
  }

  private watchViewData() {
    merge(
      this.dataSource$.pipe(tap(dataSource => (this.dataSource = dataSource))),
      this.dataSourceService$.pipe(
        tap(dataSourceService => {
          this.dataSourceService = dataSourceService;
          this.showLimit =
            dataSourceService.sidebarShowMoreThreshold ?? this.DEFAULT_VISIBLE_ITEMS_THRESHOLD;
        }),
      ),
      this.viewPageService.currentView$.pipe(
        filterBoolean,
        tap(view => this.currentView.set(view)),
      ),
    )
      .pipe(debounceAsap(), this.untilDestroyed)
      .subscribe(() => this.cdRef.detectChanges());
  }

  private watchExpandedGroups() {
    this.viewPageService.expandedSidebarGroups$
      .pipe(this.untilDestroyed)
      .subscribe(expandedGroups => {
        this.expandedGroups = expandedGroups;
        this.cdRef.detectChanges();
      });
  }

  private scrollIntoActiveView() {
    const ref = effect(() => {
      const activeItem = this.sidebarItemComponents().find(
        item => item.leftSlotContext()?.view.id === this.currentView()?.id,
      );
      if (!activeItem) return;

      activeItem.elementRef.nativeElement.scrollIntoView({ block: 'center' });
      ref.destroy();
    });
  }

  private navigateToView(viewId: string | number) {
    this.router.navigate([this.baseRoute, `${viewId}`]).then(() => this.cdRef.detectChanges());
  }
}
